import React, { useState, useEffect } from 'react';

interface AwardImage {
  filename: string;
  path: string;
  title: string;
}

const Awards: React.FC = () => {
  const [images, setImages] = useState<AwardImage[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const awardsImages: AwardImage[] = [
      {
        filename: 'award_1.jpeg',
        path: '/awards_pics/award_1.jpeg',
        title: 'Best Insightful Panel Award 2025',
      },
      {
        filename: 'award_2.jpeg',
        path: '/awards_pics/award_2.jpeg',
        title: 'Best Commanding Focus Presentation Award 2025',
      },
      {
        filename: 'award_3.jpeg',
        path: '/awards_pics/award_3.jpeg',
        title: 'Best Public Domain Use Case 2025',
      },
      {
        filename: 'award_4.jpeg',
        path: '/awards_pics/award_4.jpeg',
        title: 'Best Business Use Case Award 2025',
      },
    ];

    setImages(awardsImages);
    setLoading(false);
  }, []);

  const openLightbox = (index: number) => {
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
    document.body.style.overflow = 'unset';
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedIndex === null || images.length === 0) return;

    const newIndex =
      direction === 'prev'
        ? selectedIndex > 0
          ? selectedIndex - 1
          : images.length - 1
        : selectedIndex < images.length - 1
        ? selectedIndex + 1
        : 0;

    setSelectedIndex(newIndex);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateImage('prev');
      if (e.key === 'ArrowRight') navigateImage('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, images.length]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="mb-12 grid gap-8 items-center md:grid-cols-2">
          <div className="text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
              Awards
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto md:mx-0">
              A showcase of the Language Intelligence Conference awards and recognitions.
            </p>
            <div className="mt-6 inline-flex items-center justify-center md:justify-start space-x-3">
              <img
                src="/awards_pics/award_5.png"
                alt="Awards icon"
                className="w-14 h-14 object-contain"
                loading="lazy"
              />
              <span className="text-sm text-gray-600 max-w-xs">
                Celebrating outstanding contributions to language technology and intelligent communication.
              </span>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-lg bg-gray-100">
            <img
              src="/awards_pics/award_6.png"
              alt="Awards hero"
              className="w-full h-full object-contain md:object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <div
              key={image.filename}
              className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white"
              onClick={() => openLightbox(index)}
            >
              <div className="aspect-[4/3] bg-gray-100">
                <img
                  src={image.path}
                  alt={image.title}
                  className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="px-4 py-3 border-t border-gray-100 text-center">
                <p className="text-sm font-medium text-gray-800">{image.title}</p>
              </div>
            </div>
          ))}
        </div>

        {selectedIndex !== null && images[selectedIndex] && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
              className="absolute left-4 text-white hover:text-gray-300 transition-colors"
              aria-label="Previous image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
              className="absolute right-4 text-white hover:text-gray-300 transition-colors"
              aria-label="Next image"
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div
              className="max-w-4xl max-h-[85vh] w-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[selectedIndex].path}
                alt={images[selectedIndex].title}
                className="max-w-full max-h-[85vh] object-contain"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Awards;
